import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "workflow-run-browser" }
const _hoisted_2 = { class: "workflow-run-browser__history" }
const _hoisted_3 = { class: "workflow-run-browser__header" }
const _hoisted_4 = { class: "workflow-run-browser__title" }
const _hoisted_5 = { class: "workflow-run-browser__title" }
const _hoisted_6 = {
  key: 1,
  class: "workflow-run-browser__empty text-medium-emphasis"
}
const _hoisted_7 = { class: "workflow-run-browser__details" }

import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import FactoryRunPanel from '@/components/factories/FactoryRunPanel.vue'
import type { FactoryStreamState } from '@/composables/useFactoryStream'
import type { FactoryRunDisplay, FactoryRunRecord } from '@/types/factories'
import { toErroredFactoryRunDisplay, toHistoricalFactoryRunDisplay, toIdleFactoryRunDisplay } from '@/utils/factoryRuns'


export default /*@__PURE__*/_defineComponent({
  __name: 'FactoryRunBrowser',
  props: {
    factoryId: {},
    factoryName: {},
    liveRunState: {}
  },
  emits: ["pause", "resume", "cancel"],
  setup(__props: any) {

const props = __props



const store = useStore()
const selectedRunId = ref<string | null>(null)
const selectedSource = ref<'live' | 'history'>('history')

const hasLiveDisplay = computed<boolean>(() => (
  Boolean(props.liveRunState.runId) || props.liveRunState.status === 'error'
))

const historicalRuns = computed<FactoryRunRecord[]>(() => {
  const runs = store.getters.factoryRunsByFactory?.(props.factoryId) || []
  return runs.filter((run: FactoryRunRecord) => run.runId !== props.liveRunState.runId)
})

const isLoadingHistory = computed<boolean>(() => store.getters.isFactoryRunListLoading?.(props.factoryId) ?? false)

const selectedHistoricalRun = computed<FactoryRunRecord | undefined>(() => {
  if (!selectedRunId.value) return undefined
  return historicalRuns.value.find((run) => run.runId === selectedRunId.value)
})

const selectedHistoricalStepRuns = computed(() => {
  if (!selectedRunId.value) return []
  return store.getters.factoryStepRunsByRun?.(selectedRunId.value) || []
})

const selectedDisplayRun = computed<FactoryRunDisplay>(() => {
  if (selectedSource.value === 'live' && props.liveRunState.runId && props.liveRunState.run) {
    return toHistoricalFactoryRunDisplay(props.liveRunState.run, props.liveRunState.stepRuns)
  }

  if (selectedSource.value === 'live' && props.liveRunState.status === 'error' && props.liveRunState.error) {
    return toErroredFactoryRunDisplay(props.factoryId, props.factoryName, props.liveRunState.error)
  }

  if (selectedHistoricalRun.value) {
    return toHistoricalFactoryRunDisplay(selectedHistoricalRun.value, selectedHistoricalStepRuns.value)
  }

  return toIdleFactoryRunDisplay(props.factoryId, props.factoryName)
})

const liveRunSubtitle = computed(() => {
  if (!props.liveRunState.runId) {
    return props.liveRunState.error || props.liveRunState.status
  }
  return `${props.liveRunState.status}${props.liveRunState.runId ? ` • ${props.liveRunState.runId.slice(0, 8)}` : ''}`
})

const liveRunTitle = computed(() => {
  switch (props.liveRunState.status) {
    case 'completed':
      return 'Completed Run'
    case 'cancelled':
      return 'Cancelled Run'
    case 'error':
      return 'Failed Run'
    case 'paused':
      return 'Paused Run'
    case 'queued':
    case 'running':
    default:
      return 'Current Run'
  }
})

const liveRowLabel = computed(() => (
  props.liveRunState.status === 'running' || props.liveRunState.status === 'queued'
    ? 'LIVE'
    : props.liveRunState.status.toUpperCase()
))

async function fetchRunHistory() {
  await store.dispatch('fetchFactoryRunsForFactory', { factoryId: props.factoryId, limit: 20, offset: 0, append: false })
}

async function selectHistoricalRun(runId: string) {
  selectedSource.value = 'history'
  selectedRunId.value = runId
  await store.dispatch('fetchFactoryRunSteps', { runId, limit: 200, offset: 0, append: false })
}

function selectLiveRun() {
  selectedSource.value = 'live'
  selectedRunId.value = props.liveRunState.runId
}

function pickInitialSelection() {
  if (hasLiveDisplay.value) {
    selectLiveRun()
    return
  }
  const firstHistorical = historicalRuns.value[0]
  if (firstHistorical) {
    selectedSource.value = 'history'
    selectedRunId.value = firstHistorical.runId
    void store.dispatch('fetchFactoryRunSteps', { runId: firstHistorical.runId, limit: 200, offset: 0, append: false })
    return
  }
  selectedSource.value = 'history'
  selectedRunId.value = null
}

function formatRunTitle(run: FactoryRunRecord) {
  return new Date(run.startedAt).toLocaleString()
}

function formatRunSubtitle(run: FactoryRunRecord) {
  const steps = run.cursor.iterationCount
  const tokens = run.aggregateUsage?.totalTokens || 0
  return `${steps} step${steps === 1 ? '' : 's'} • ${tokens} tokens`
}

function statusColor(status: FactoryRunRecord['status'] | FactoryStreamState['status']) {
  switch (status) {
    case 'running': return 'primary'
    case 'queued': return 'grey'
    case 'paused': return 'warning'
    case 'completed': return 'success'
    case 'cancelled': return 'warning'
    case 'error': return 'error'
    default: return 'grey'
  }
}

watch(
  () => props.factoryId,
  async () => {
    selectedRunId.value = null
    selectedSource.value = 'history'
    await fetchRunHistory()
    pickInitialSelection()
  }
)

watch(
  () => props.liveRunState.runId,
  async (runId) => {
    if (!runId) return
    selectedSource.value = 'live'
    selectedRunId.value = runId
    await fetchRunHistory()
  }
)

watch(
  () => `${props.liveRunState.status}:${props.liveRunState.error || ''}`,
  () => {
    if (props.liveRunState.status === 'error' && !props.liveRunState.runId) {
      selectedSource.value = 'live'
      selectedRunId.value = null
    }
  }
)

watch(
  () => historicalRuns.value.map((run) => run.runId).join('|'),
  () => {
    if (selectedSource.value === 'history' && selectedRunId.value && historicalRuns.value.some((run) => run.runId === selectedRunId.value)) {
      return
    }
    if (selectedSource.value === 'live' && hasLiveDisplay.value) {
      return
    }
    pickInitialSelection()
  }
)

onMounted(async () => {
  await fetchRunHistory()
  pickInitialSelection()
})

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("aside", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "text-overline mb-1" }, "Run History", -1)),
        (isLoadingHistory.value)
          ? (_openBlock(), _createBlock(_component_v_progress_circular, {
              key: 0,
              density: "compact",
              size: "18",
              width: "2",
              indeterminate: "",
              color: "primary"
            }))
          : _createCommentVNode("", true)
      ]),
      _createVNode(_component_v_list, {
        class: "workflow-run-browser__list",
        density: "compact",
        "bg-color": "transparent"
      }, {
        default: _withCtx(() => [
          (hasLiveDisplay.value)
            ? (_openBlock(), _createBlock(_component_v_list_item, {
                key: 0,
                active: selectedSource.value === 'live',
                density: "compact",
                class: "workflow-run-browser__item",
                onClick: selectLiveRun
              }, {
                title: _withCtx(() => [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("span", null, _toDisplayString(liveRunTitle.value), 1),
                    _createVNode(_component_v_chip, {
                      size: "x-small",
                      density: "compact",
                      color: statusColor(_ctx.liveRunState.status),
                      class: "ml-2"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(liveRowLabel.value), 1)
                      ]),
                      _: 1
                    }, 8, ["color"])
                  ])
                ]),
                subtitle: _withCtx(() => [
                  _createElementVNode("span", null, _toDisplayString(liveRunSubtitle.value), 1)
                ]),
                _: 1
              }, 8, ["active"]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(historicalRuns.value, (run) => {
            return (_openBlock(), _createBlock(_component_v_list_item, {
              key: run.runId,
              active: selectedSource.value === 'history' && selectedRunId.value === run.runId,
              density: "compact",
              class: "workflow-run-browser__item",
              onClick: ($event: any) => (selectHistoricalRun(run.runId))
            }, {
              title: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("span", null, _toDisplayString(formatRunTitle(run)), 1),
                  _createVNode(_component_v_chip, {
                    size: "x-small",
                    density: "compact",
                    color: statusColor(run.status),
                    class: "ml-2"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(run.status), 1)
                    ]),
                    _: 2
                  }, 1032, ["color"])
                ])
              ]),
              subtitle: _withCtx(() => [
                _createElementVNode("span", null, _toDisplayString(formatRunSubtitle(run)), 1)
              ]),
              _: 2
            }, 1032, ["active", "onClick"]))
          }), 128)),
          (!hasLiveDisplay.value && historicalRuns.value.length === 0 && !isLoadingHistory.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, " No runs yet. "))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _createElementVNode("section", _hoisted_7, [
      _createVNode(FactoryRunPanel, {
        run: selectedDisplayRun.value,
        onPause: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('pause'))),
        onResume: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('resume'))),
        onCancel: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('cancel')))
      }, null, 8, ["run"])
    ])
  ]))
}
}

})